import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Shield, Eye, EyeOff, Bell, Lock, Loader2, Phone, Mail, User } from 'lucide-react';
import PushNotificationToggle from '@/components/PushNotificationToggle';

const PrivacySettings = ({ user, onBack }) => {
  const [settings, setSettings] = useState({ 
    show_owner_info: true,
    show_name: true,
    show_email: true,
    show_phone: true,
    alert_on_search: true,
    require_contact_for_alert: false
  });
  const [searchAlerts, setSearchAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Password change state
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchSearchAlerts();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get('/users/privacy-settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSearchAlerts = async () => {
    try {
      const response = await axios.get('/users/search-alerts');
      setSearchAlerts(response.data);
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
    }
  };

  const handleTogglePrivacy = async (checked) => {
    try {
      await axios.put('/users/privacy-settings', { show_owner_info: checked });
      setSettings(prev => ({ ...prev, show_owner_info: checked }));
      toast.success('Privacy settings updated');
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const updateGranularSetting = async (settingName, value) => {
    try {
      await axios.put('/users/privacy-settings', { [settingName]: value });
      setSettings(prev => ({ ...prev, [settingName]: value }));
      toast.success('Setting updated');
    } catch (error) {
      toast.error('Failed to update setting');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    // Validate passwords
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordData.new_password.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.put('/auth/change-password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      toast.success('Password changed successfully!');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to change password';
      toast.error(errorMsg);
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div data-testid="privacy-settings" className="space-y-6">
      {/* Change Password Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5" />
            Change Password
          </CardTitle>
          <CardDescription>
            Update your account password
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current_password">Current Password</Label>
              <Input
                id="current_password"
                type="password"
                value={passwordData.current_password}
                onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                placeholder="Enter current password"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new_password">New Password</Label>
              <Input
                id="new_password"
                type="password"
                value={passwordData.new_password}
                onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                placeholder="Enter new password (min 6 characters)"
                required
                minLength={6}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm_password">Confirm New Password</Label>
              <Input
                id="confirm_password"
                type="password"
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                placeholder="Confirm new password"
                required
              />
            </div>
            <Button 
              type="submit" 
              disabled={changingPassword || !passwordData.current_password || !passwordData.new_password || !passwordData.confirm_password}
              className="w-full"
            >
              {changingPassword ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Changing Password...
                </>
              ) : (
                'Change Password'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Privacy Settings
          </CardTitle>
          <CardDescription>
            Control what information is shown when someone searches your pet&apos;s microchip
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Master Toggle */}
          <div className="flex items-center justify-between p-4 bg-stone-50 rounded-lg">
            <div className="space-y-1">
              <Label className="text-base font-semibold flex items-center gap-2">
                {settings.show_owner_info ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                Show My Contact Information
              </Label>
              <p className="text-sm text-stone-600">
                Master switch to control all contact information visibility
              </p>
            </div>
            <Switch
              data-testid="privacy-toggle"
              checked={settings.show_owner_info}
              onCheckedChange={handleTogglePrivacy}
            />
          </div>

          {/* Granular Controls */}
          {settings.show_owner_info && (
            <div className="space-y-4 border rounded-lg p-4">
              <p className="text-sm font-semibold text-stone-700">Choose which information to share:</p>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-stone-500" />
                  <Label>Show my name</Label>
                </div>
                <Switch
                  checked={settings.show_name !== false}
                  onCheckedChange={(checked) => updateGranularSetting('show_name', checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-stone-500" />
                  <Label>Show my email</Label>
                </div>
                <Switch
                  checked={settings.show_email !== false}
                  onCheckedChange={(checked) => updateGranularSetting('show_email', checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-stone-500" />
                  <Label>Show my phone number</Label>
                </div>
                <Switch
                  checked={settings.show_phone !== false}
                  onCheckedChange={(checked) => updateGranularSetting('show_phone', checked)}
                />
              </div>
            </div>
          )}

          {/* Alert Settings */}
          <div className="space-y-4 border rounded-lg p-4">
            <p className="text-sm font-semibold text-stone-700 flex items-center gap-2">
              <Bell className="w-4 h-4" />
              Search Alert Settings
            </p>
            
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label>Alert me when microchip is searched</Label>
                <p className="text-xs text-stone-500">Receive notifications when someone searches your pet</p>
              </div>
              <Switch
                checked={settings.alert_on_search !== false}
                onCheckedChange={(checked) => updateGranularSetting('alert_on_search', checked)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label>Require searcher contact info</Label>
                <p className="text-xs text-stone-500">Searchers must provide their contact details to alert you</p>
              </div>
              <Switch
                checked={settings.require_contact_for_alert === true}
                onCheckedChange={(checked) => updateGranularSetting('require_contact_for_alert', checked)}
              />
            </div>
          </div>

          <div className="p-4 bg-blue-50 rounded-lg space-y-2">
            <p className="text-sm font-semibold text-blue-800">How It Works:</p>
            <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
              <li><strong>Privacy ON:</strong> Your selected contact details are shown to searchers</li>
              <li><strong>Privacy OFF:</strong> People see a &quot;Contact Owner&quot; button that forwards messages to you</li>
              <li><strong>Alert notifications:</strong> You&apos;ll see alerts in your dashboard when your microchip is searched</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Push Notifications Card */}
      <PushNotificationToggle />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Microchip Search Alerts
          </CardTitle>
          <CardDescription>
            Recent searches of your pet&apos;s microchip number
          </CardDescription>
        </CardHeader>
        <CardContent>
          {searchAlerts.length === 0 ? (
            <p className="text-sm text-stone-600 py-4">No searches recorded yet</p>
          ) : (
            <div data-testid="search-alerts-list" className="space-y-3">
              {searchAlerts.slice(0, 10).map((alert) => (
                <div key={alert.alert_id} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                  <div>
                    <p className="text-sm font-semibold">Microchip: {alert.microchip_number}</p>
                    <p className="text-xs text-stone-600">
                      {new Date(alert.searched_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="text-xs text-stone-500">
                    IP: {alert.ip_address}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PrivacySettings;
